import React, { useState } from 'react';
import RequestBuilder from 'Resources/RequestBuilder';
import T from 'Components/Helpers/translate';
import { MultiSelect } from 'App/components/ListView/ListViewRenderHelpers';
import NotificationService from 'App/babel/NotificationService';
import getAngularModule from 'App/babel/angularHelpers/getAngularModule';
import openModal from 'App/services/Modal';
import { Block, Button as _Button, ButtonGroup } from '@upsales/components';
import MultiActions from 'Resources/MultiActions';

const Button = _Button as React.FC<React.ComponentProps<typeof _Button>>;

type ActionType = 'activate' | 'deactivate';

const ActionSelector = ({ selection }: { selection: { current: ActionType } }) => {
	const [selected, setSelected] = useState<ActionType>('deactivate');

	const handleChange = (action: ActionType) => {
		setSelected(action);
		selection.current = action;
	};

	return React.createElement(
		Block,
		null,
		React.createElement(
			Block,
			{ space: 'mbl' },
			React.createElement(
				ButtonGroup,
				null,
				React.createElement(
					Button,
					{
						type: selected === 'activate' ? undefined : 'lined',
						shadow: 'none',
						onClick: () => handleChange('activate')
					},
					T('toggleContactsActive.activateLabel')
				),
				React.createElement(
					Button,
					{
						type: selected === 'deactivate' ? undefined : 'lined',
						shadow: 'none',
						onClick: () => handleChange('deactivate')
					},
					T('toggleContactsActive.deactivateLabel')
				)
			)
		)
	);
};

export default async function toggleContactsActive(
	requestBuilder: RequestBuilder,
	entity: string,
	multiSelect: MultiSelect,
	customerId: number
) {
	const AppService = getAngularModule('AppService');
	const userEmail = AppService.getSelf().email;

	const contactCount = multiSelect.selected;
	const displayAmount =
		contactCount > 1
			? `${contactCount} ${T('default.contacts').toLowerCase()}`
			: `1 ${T('default.contact').toLowerCase()}`;

	// Mutable ref to capture selected action from the radio buttons
	const selection: { current: ActionType } = { current: 'deactivate' };

	const confirmed = await new Promise<boolean>(resolve => {
		openModal('Alert', {
			title: T('toggleContactsActive.title', { displayAmount }),
			confirmButtonText: 'toggleContactsActive.confirm',
			cancelButtonText: 'default.cancel',
			confirmationMode: 'text',
			confirmText: userEmail,
			customBody: React.createElement(ActionSelector, { selection }),
			onClose: (result?: boolean) => {
				resolve(result || false);
			}
		});
	});

	if (!confirmed) {
		return;
	}

	const isActivate = selection.current === 'activate';
	const properties = [{ name: 'Active', value: isActivate ? '1' : '0' }];

	try {
		const response = await MultiActions.customer(customerId).updateContact(
			entity,
			multiSelect.selected,
			properties,
			requestBuilder.build(),
			{}
		);

		if (multiSelect.selected <= 10) {
			setTimeout(() => {
				NotificationService.add({
					style: NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.saved',
					body: isActivate
						? T('multi.activateContacts.done', { count: multiSelect.selected })
						: T('multi.inactivateContacts.done', { count: multiSelect.selected })
				});
			}, 2000);
		} else {
			NotificationService.add({
				style: NotificationService.style.SUCCESS,
				icon: 'check',
				title: 'default.saved',
				body: isActivate ? T('updateQued.activateContacts') : T('updateQued.inactivateContacts')
			});
		}

		return response;
	} catch (e) {
		NotificationService.add({
			style: NotificationService.style.ERROR,
			icon: 'times',
			title: 'default.error',
			body: e
		});
		throw e;
	}
}
